import { useMemo, useRef, useState } from "react";

import { TableContainer } from "@mui/material";
import {
  DataGrid,
  GridCellParams,
  GridRowHeightReturnValue,
  GridRowParams,
} from "@mui/x-data-grid";
import { useConfirm } from "material-ui-confirm";

import { FeedbackType } from "../../../../../API";
import MediaCardDialogNew from "../../../../../common/components/media/MediaCardDialogNew";
import TableNoDataOverlay from "../../../../../common/components/table/TableNoDataOverlay";
import confirmDialogStyleOptions from "../../../../../common/helpers/confirmDialogParams";
import { useUpdateTimestreamData } from "../../../../../common/hooks/useUpdateTimestreamData";
import { dataGridServerStyles } from "../../../../../common/providers/theme/design-tokens/DataGrid/dataGridServer.styles";
import { DEFAULT_COLUMN_HEADER_HEIGHT } from "../../../../../common/variables/common";
import {
  errorNotification,
  successNotification,
} from "../../../../../common/variables/notification";
import { TablePaperContainer } from "../../../../../leak-finder/components/VideosTable";
import FeedbackDialog from "../../feedback/FeedbackDialog";
import { MITIGATED_RISK_SEPARATOR } from "../common/events.constants";
import { IRecentEventsTableRow } from "../common/events.models";
import { useGetRecentEventsTableColumns } from "./hooks/useGetRecentEventsTableColumns";
import {
  DEFAULT_RECENT_ACTIVITIES_PAGE_SIZE,
  DEFAULT_RECENT_ACTIVITIES_PAGE_SIZE_OPTIONS,
  useGetRecentEventsTableRows,
} from "./hooks/useGetRecentEventsTableRows";

const RecentEventsTableServer = (): JSX.Element => {
  const confirm = useConfirm();

  const { columns } = useGetRecentEventsTableColumns();

  const {
    rows,
    loading: getRecentActivitiesLoading,
    paginationModel,
    handlePaginationModelChange,
    hasNextPage,
    isActivePageChange,
    queryInput,
  } = useGetRecentEventsTableRows();

  const { updateTimestreamData, loading: updateTimestreamDataLoading } =
    useUpdateTimestreamData();

  const keyFrameRef = useRef(false);
  const feedbackDialogRef = useRef("");
  const thumbDownRef = useRef(false);
  const rowDataRef = useRef<IRecentEventsTableRow>();

  const [open, setOpen] = useState(false);
  const [signedUrl, setSignedUrl] = useState("");
  const [openFeedbackDialog, setOpenFeedbackDialog] = useState(false);
  const [submitFeedbackLoading, setSubmitFeedbackLoading] = useState(false);

  const handleKeyFrameClick = (params: GridCellParams): void => {
    keyFrameRef.current = true;

    const mediaUrl = params?.row?.signedUrlKeyFrame ?? "";

    setSignedUrl(mediaUrl);

    setOpen(true);
  };

  const handleRowClick = (params: GridRowParams): void => {
    rowDataRef.current = params?.row as IRecentEventsTableRow;

    // prevent the row click event from firing if the keyFrame was clicked
    if (keyFrameRef.current) {
      return;
    }

    // prevent the row click event from firing if the feedback was clicked
    if (feedbackDialogRef.current) {
      return;
    }

    // prevent the row click event from firing if the thumb down was clicked
    if (thumbDownRef.current) {
      return;
    }

    const mediaUrl = params?.row?.signedUrlMediaOutput ?? "";

    setSignedUrl(mediaUrl);

    setOpen(true);
  };

  const handleCloseDialog = (): void => {
    setOpen(false);

    setSignedUrl("");

    // reset the keyFrameRef
    keyFrameRef.current = false;

    rowDataRef.current = undefined;
  };

  const handleFeedbackDialogClose = (): void => {
    feedbackDialogRef.current = "";

    setOpenFeedbackDialog(false);
  };

  const handleSubmitFeedback = (
    params: IRecentEventsTableRow,
    mitigatedRisk: string[]
  ): void => {
    setSubmitFeedbackLoading(true);

    const { model, location, keyFrame } = params;

    const time = params.time;

    const customerId = location?.split("#").slice(0, 2).join("#");

    const nodeId = keyFrame?.split("/")[1]?.replace(/_/g, "#") || "";

    const input = {
      feedback: FeedbackType.POSITIVE,
      mitigatedRisk: mitigatedRisk.join(MITIGATED_RISK_SEPARATOR),
      model: model,
      customerId: customerId,
      locationId: location,
      nodeId: nodeId,
      measureName: model,
      time: time,
    };

    updateTimestreamData(params?.rowId, input, queryInput)
      .then((response): void => {
        if (!response.errors) {
          successNotification("Your selections have been saved successfully");

          handleFeedbackDialogClose();

          setSubmitFeedbackLoading(false);
        }
      })
      .catch((error: Error): void => {
        errorNotification("Failed to save selections. Please try again.");

        setSubmitFeedbackLoading(false);

        console.error(error);
      });
  };

  const handleThumbUpClick = (row: IRecentEventsTableRow): void => {
    if (!row) return;

    feedbackDialogRef.current = row?.rowId;

    setOpenFeedbackDialog(true);
  };

  const handleThumbsDownClick = (
    id: string,
    row: IRecentEventsTableRow,
    closeDialog?: boolean
  ) => {
    if (!row) return;

    thumbDownRef.current = true;

    if (!row.feedback) {
      handleNegativeFeedback(id, row);

      return;
    }

    confirm({
      title: "Are you sure you want to change to negative feedback?",
      description:
        "Switching to negative feedback will remove all mitigated risk selections.",
      confirmationText: "Confirm",
      cancellationText: "Cancel",
      ...confirmDialogStyleOptions,
      confirmationButtonProps: {
        ...confirmDialogStyleOptions.confirmationButtonProps,
        color: "primary",
      },
    })
      .then((): void => {
        handleNegativeFeedback(id, row);
      })
      .catch((): void => {
        if (closeDialog) {
          thumbDownRef.current = false;
        }
      });
  };

  const handleNegativeFeedback = (id: string, row: IRecentEventsTableRow) => {
    const { model, location, keyFrame } = row;

    const time = row?.time;

    const customerId = location?.split("#").slice(0, 2).join("#");

    const nodeId = keyFrame?.split("/")[1]?.replace(/_/g, "#") || "";

    const input = {
      feedback: FeedbackType.NEGATIVE,
      mitigatedRisk: " ", // fix this, provide the correct value
      model,
      customerId,
      locationId: location,
      nodeId,
      measureName: model,
      time,
    };

    updateTimestreamData(id, input, queryInput)
      .then((response): void => {
        if (!response.errors) {
          successNotification("Your Feedback has been updated");

          thumbDownRef.current = false;
        }
      })
      .catch((error: Error): void => {
        errorNotification("Failed to update. Please try again.");

        console.error(error);
      });
  };

  const memoizedRows = useMemo((): IRecentEventsTableRow[] => {
    return rows.map(row => {
      return {
        ...row,
        onKeyFrameClick: handleKeyFrameClick,
        onThumbUpClick: handleThumbUpClick,
        onThumbsDownClick: handleThumbsDownClick,
      };
    });
  }, [rows]);

  const loading = getRecentActivitiesLoading || updateTimestreamDataLoading;

  const rowData = memoizedRows?.find(
    row => row.rowId === feedbackDialogRef.current
  );

  const mediaDialogData = memoizedRows?.find(
    row => row.rowId === rowDataRef.current?.rowId
  );

  return (
    <TableContainer component={TablePaperContainer}>
      {open && mediaDialogData && (
        <MediaCardDialogNew
          rowData={mediaDialogData}
          fullWidth
          signedUrl={signedUrl}
          open={open}
          submitFeedbackLoading={submitFeedbackLoading}
          onClose={handleCloseDialog}
          onFeedbackSubmitClick={handleSubmitFeedback}
        />
      )}
      {openFeedbackDialog && (
        <FeedbackDialog
          submitLoading={submitFeedbackLoading}
          rowData={rowData}
          open={openFeedbackDialog}
          onClose={handleFeedbackDialogClose}
          onSubmitClick={handleSubmitFeedback}
        />
      )}
      <DataGrid
        sx={{
          ...dataGridServerStyles.sx,
          "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
            py: "8px",
          },
          "& .MuiButtonBase-root.MuiIconButton-root:last-child": {
            pointerEvents: isActivePageChange ? "auto" : "none",
            cursor: isActivePageChange ? "pointer" : "none",
            backgroundColor: "transparent",
            color: theme =>
              isActivePageChange ? "inherit" : theme.palette.action.disabled,
          },
        }}
        columnHeaderHeight={DEFAULT_COLUMN_HEADER_HEIGHT}
        rows={memoizedRows}
        columns={columns}
        slots={{
          noRowsOverlay: TableNoDataOverlay,
        }}
        rowCount={-1}
        onRowClick={handleRowClick}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        getRowId={(row: { rowId: string }): string => row.rowId}
        getRowHeight={(): GridRowHeightReturnValue => 96}
        rowHeight={96}
        loading={loading}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        paginationMeta={{ hasNextPage }}
        pageSizeOptions={DEFAULT_RECENT_ACTIVITIES_PAGE_SIZE_OPTIONS}
        sortingOrder={["desc", "asc"]}
        initialState={{
          sorting: {
            sortModel: [
              {
                field: "timestamp",
                sort: "desc",
              },
            ],
          },
          pagination: {
            paginationModel: {
              pageSize: DEFAULT_RECENT_ACTIVITIES_PAGE_SIZE,
              page: 0,
            },
            rowCount: -1,
          },
        }}
      />
    </TableContainer>
  );
};

export default RecentEventsTableServer;
